import {
  Analytics01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowRight02Icon,
  Award01Icon,
  BookOpen01Icon,
  Calendar02Icon,
  ChampionIcon,
  GraduationScrollIcon,
  Medal01Icon,
  Rocket01Icon,
  StarAward01Icon,
  TaskDone01Icon,
  User02Icon,
  HourglassIcon,
  ShieldUserIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { VariantProps } from 'class-variance-authority';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge, type badgeVariants } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EllipsisText } from '@/components/ui/ellipsis-text';
import { Progress } from '@/components/ui/progress';
import { ActivityRings } from '@/components/ui/progress-ring';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/auth-context';
import { usePermissions } from '@/hooks/usePermissions';
import { gamificationApi, maintenanceTicketsApi, usersApi } from '@/lib/api-client';
import { cn } from '@/lib/utils';

// Status badge configuration
type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  open: { label: 'Open', variant: 'outline' as const },
  in_progress: { label: 'In Progress', variant: 'info' as const },
  'in-progress': { label: 'In Progress', variant: 'info' as const },
  completed: { label: 'Completed', variant: 'success' as const },
  closed: { label: 'Closed', variant: 'secondary' as const },
  pending: { label: 'Pending', variant: 'warning' as const },
  cancelled: { label: 'Cancelled', variant: 'destructive' as const },
  canceled: { label: 'Cancelled', variant: 'destructive' as const },
};

const PRIORITY_CONFIG: Record<string, StatusConfig> = {
  low: { label: 'Low', variant: 'outline' as const },
  medium: { label: 'Medium', variant: 'default' as const },
  high: { label: 'High', variant: 'warning' as const },
  critical: { label: 'Critical', variant: 'destructive' as const },
  urgent: { label: 'Urgent', variant: 'destructive' as const },
};

function getStatusBadge(status?: string | null) {
  const normalizedStatus = status?.toLowerCase().replace('-', '_') ?? 'open';
  const config = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.open;
  return { label: config.label, variant: config.variant };
}

function getPriorityBadge(priority?: string | null) {
  const normalizedPriority = priority?.toLowerCase() ?? 'medium';
  const config = PRIORITY_CONFIG[normalizedPriority] || PRIORITY_CONFIG.medium;
  return { label: config.label, variant: config.variant };
}

// Pagination utility function
function usePagination<T>(items: T[], itemsPerPage: number = 5) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(items.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = items.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Reset to page 1 when items length changes
  useEffect(() => {
    setCurrentPage(1);
  }, []);

  // Adjust if current page exceeds total pages
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return {
    paginatedItems,
    currentPage,
    totalPages,
    goToPage,
    goToNextPage,
    goToPrevPage,
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1,
  };
}

// Pagination controls component
function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between mt-3 pt-3 border-t">
      <div className="text-xs text-muted-foreground">
        {t('home.pagination.pageOf', { current: currentPage, total: totalPages })}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={!hasPrev}
          className="h-7 w-7 p-0"
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
        </Button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }

          return (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(pageNum)}
              className="h-7 w-7 p-0 text-xs"
            >
              {pageNum}
            </Button>
          );
        })}
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={!hasNext}
          className="h-7 w-7 p-0"
        >
          <HugeiconsIcon icon={ArrowRight01Icon} size={14} />
        </Button>
      </div>
    </div>
  );
}

// Tenure calculation utilities
interface Tenure {
  years: number;
  months: number;
  days: number;
  totalDays: number;
}

function calculateTenure(joinedAt: Date | string | null | undefined): Tenure | null {
  if (!joinedAt) return null;

  const joinDate = new Date(joinedAt);
  const now = new Date();

  // Check if the date is valid
  if (Number.isNaN(joinDate.getTime())) return null;

  let years = now.getFullYear() - joinDate.getFullYear();
  let months = now.getMonth() - joinDate.getMonth();
  let days = now.getDate() - joinDate.getDate();

  // Adjust for negative days
  if (days < 0) {
    months--;
    const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += lastMonth.getDate();
  }

  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }

  // Calculate total days
  const totalDays = Math.floor((now.getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24));

  return { years, months, days, totalDays };
}

function getTenureDisplayShort(tenure: Tenure): string {
  if (tenure.years > 0) {
    return `${tenure.years}y ${tenure.months}mo`;
  }
  if (tenure.months > 0) {
    return `${tenure.months}mo ${tenure.days}d`;
  }
  return `${tenure.days}d`;
}

interface MotivationalQuote {
  quote: string;
  author?: string;
}

function getMotivationalQuote(tenure: Tenure | null, t: any): MotivationalQuote {
  if (!tenure) {
    return { quote: t('home.quotes.welcome') };
  }

  const quotes = [
    t('home.quotes.welcome'),
    t('home.quotes.newBeginner'),
    t('home.quotes.firstStep'),
    t('home.quotes.freshStart'),
  ];
  const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
  return { quote: randomQuote };
}

// Native-style mobile components

interface MobileHeaderProps {
  userName: string;
  points: number;
  level: number;
  levelProgress: number;
  nextLevel: number | null;
  completedTrainings: number;
  rank: number;
  avatarUrl?: string;
  tenure?: Tenure | null;
  competenceData?: any;
}

function MobileNativeHeader({
  userName,
  points,
  level,
  levelProgress,
  completedTrainings,
  rank,
  avatarUrl,
  tenure,
  competenceData,
}: MobileHeaderProps) {
  const { t } = useTranslation();
  const initials = userName.slice(0, 2).toUpperCase();

  return (
    <div className="md:hidden sticky z-10 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="px-4 py-3">
        {/* Top row: Avatar + Greeting + Level Ring */}
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12 border-2 border-primary/20">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={userName} />}
            <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-primary to-accent text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">{t('home.mobile.welcome')}</p>
            <h1 className="text-lg font-semibold truncate">{userName}</h1>
          </div>
          {/* Level Ring */}
          <div className="relative h-14 w-14 shrink-0">
            <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="3"
                strokeOpacity="0.3"
              />
              <circle
                cx="18"
                cy="18"
                r="15.5"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="3"
                strokeDasharray={`${levelProgress} 100`}
                strokeLinecap="round"
                className="transition-all duration-500 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[10px] text-muted-foreground">LVL</span>
              <span className="text-sm font-bold">{level}</span>
            </div>
          </div>
        </div>

        {/* Second row: Quick stats pills */}
        <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-hide -mx-4 px-4 pt-4">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 shrink-0">
            <HugeiconsIcon icon={GraduationScrollIcon} size={14} className="text-emerald-500" />
            <span className="text-sm font-semibold">
              {competenceData?.competence.activeQualifications ?? 0}/
              {competenceData?.competence.totalQualifications ?? 0}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 shrink-0">
            <HugeiconsIcon icon={ChampionIcon} size={14} className="text-primary" />
            <span className="text-sm font-semibold">#{rank}</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 shrink-0">
            <HugeiconsIcon icon={StarAward01Icon} size={14} className="text-amber-500" />
            <span className="text-sm font-semibold">{points.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 shrink-0">
            <HugeiconsIcon icon={TaskDone01Icon} size={14} className="text-blue-500" />
            <span className="text-sm font-semibold">
              {completedTrainings} {t('home.mobile.done')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface NativeListSectionProps {
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

function NativeListSection({ title, badge, children, className }: NativeListSectionProps) {
  return (
    <div className={cn('md:hidden', className)}>
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </h2>
        {badge}
      </div>
      <div className="mx-3 bg-muted/30 rounded-2xl overflow-hidden border border-border/50">
        {children}
      </div>
    </div>
  );
}

interface NativeListCellProps {
  icon?: React.ReactNode;
  iconClassName?: string;
  title: string | React.ReactNode;
  detail?: string;
  value?: React.ReactNode;
  badge?: React.ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  className?: string;
}

function NativeListCell({
  icon,
  iconClassName,
  title,
  detail,
  value,
  badge,
  chevron = true,
  onClick,
  className,
}: NativeListCellProps) {
  const Content = (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 active:bg-accent/50 transition-colors cursor-pointer',
        'border-b border-border/50 last:border-b-0',
        onClick && 'active:scale-[0.98] transition-transform',
        className,
      )}
    >
      {icon && (
        <div
          className={cn(
            'h-9 w-9 rounded-xl flex items-center justify-center shrink-0',
            iconClassName,
          )}
        >
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {typeof title === 'string' ? (
            <span className="text-sm font-medium truncate">{title}</span>
          ) : (
            <div className="text-sm font-medium">{title}</div>
          )}
          {badge}
        </div>
        {detail && <p className="text-xs text-muted-foreground truncate mt-0.5">{detail}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {value && <div className="text-sm">{value}</div>}
        {chevron && (
          <HugeiconsIcon icon={ArrowRight02Icon} size={16} className="text-muted-foreground" />
        )}
      </div>
    </div>
  );

  if (onClick) {
    return <div onClick={onClick}>{Content}</div>;
  }
  return Content;
}

interface NativeProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  color?: 'primary' | 'success' | 'warning' | 'accent';
}

function NativeProgressBar({ value, label, color = 'primary' }: NativeProgressBarProps) {
  const colorClasses = {
    primary: 'bg-primary',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    accent: 'bg-accent',
  };

  return (
    <div className="space-y-1">
      {label && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-xs font-medium">{Math.round(value)}%</span>
        </div>
      )}
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            colorClasses[color],
          )}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    </div>
  );
}

interface MobileStatCardProps {
  label: string;
  value: string | number;
  icon: any;
  color: string;
  bg: string;
  trend?: number;
}

function _MobileStatCard({ label, value, icon, color, bg, trend }: MobileStatCardProps) {
  return (
    <div className="flex-1 min-w-0 p-3 rounded-2xl bg-card border border-border/50">
      <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center mb-2', bg)}>
        <HugeiconsIcon icon={icon} size={16} className={color} />
      </div>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className="text-lg font-bold tabular-nums truncate">{value}</p>
      {trend !== undefined && (
        <div
          className={cn(
            'flex items-center gap-0.5 text-[10px] mt-1',
            trend > 0 ? 'text-emerald-500' : trend < 0 ? 'text-red-500' : 'text-muted-foreground',
          )}
        >
          <HugeiconsIcon
            icon={Analytics01Icon}
            size={10}
            className={trend < 0 ? 'rotate-180' : ''}
          />
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
  );
}

function Home() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Show waiting screen if user has no assigned role (no permissions)
  const hasRole = permissions.length > 0;

  const profileQuery = useQuery({
    queryKey: ['gamification-profile', user?.id],
    queryFn: () => gamificationApi.getProfile(user!.id),
    enabled: !!user?.id,
  });

  const levelsQuery = useQuery({
    queryKey: ['gamification-levels'],
    queryFn: gamificationApi.listLevels,
  });

  const leaderboardQuery = useQuery({
    queryKey: ['gamification-leaderboard'],
    queryFn: gamificationApi.getLeaderboard,
    refetchInterval: 60000, // Refetch every minute
  });

  const competenceQuery = useQuery({
    queryKey: ['gamification-competence', user?.id],
    queryFn: () => gamificationApi.getCompetenceProfile(user!.id),
    enabled: !!user?.id,
  });

  const competitionsQuery = useQuery({
    queryKey: ['gamification-competitions'],
    queryFn: () => gamificationApi.getCompetitions(),
    refetchInterval: 60000,
  });

  // Fetch user profile to get joinedAt for tenure calculation
  const userQuery = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: () => usersApi.getById(user!.id),
    enabled: !!user?.id,
  });

  // Calculate tenure based on joinedAt
  const tenure = useMemo(() => {
    const joinedAt = userQuery.data?.joinedAt;
    return calculateTenure(joinedAt ?? null);
  }, [userQuery.data?.joinedAt]);

  const motivationalQuote = useMemo(() => getMotivationalQuote(tenure, t), [tenure, t]);

  // const rulesQuery = useQuery({
  //   queryKey: ['gamification-rules'],
  //   queryFn: gamificationApi.listRules,
  // })

  // const [selectedRuleId, setSelectedRuleId] = useState<string>('')

  // const ruleTestMutation = useMutation({
  //   mutationFn: (ruleId: string) => gamificationApi.testRule(ruleId, { preview: true }),
  // })

  const pointsTotal = profileQuery.data?.points.totalPoints ?? 0;
  const currentLevel = profileQuery.data?.points.level;
  const levels = levelsQuery.data ?? [];
  const nextLevel = useMemo(() => {
    if (!levels.length) return null;
    const sorted = [...levels].sort((a, b) => a.minPoints - b.minPoints);
    const higher = sorted.find((lvl) => lvl.minPoints > pointsTotal);
    return higher ?? null;
  }, [levels, pointsTotal]);

  const levelProgress = useMemo(() => {
    if (!currentLevel && !nextLevel) return 0;
    if (!nextLevel || !currentLevel) return 100;
    const gained = Math.max(
      0,
      pointsTotal - nextLevel.minPoints + (nextLevel.minPoints - currentLevel.minPoints),
    );
    const span = nextLevel.minPoints - (currentLevel?.minPoints ?? 0);
    return Math.min(100, span > 0 ? (gained / span) * 100 : 100);
  }, [currentLevel, nextLevel, pointsTotal]);

  const badges = profileQuery.data?.badges ?? [];
  const trainings = profileQuery.data?.trainings ?? [];
  // const ruleOptions = rulesQuery.data ?? []

  // Server-side pagination state for assigned tickets
  const [assignedTicketsPage, setAssignedTicketsPage] = useState(1);
  const assignedTicketsPerPage = 5;

  // Fetch assigned tickets with server-side pagination
  const assignedTicketsQuery = useQuery({
    queryKey: ['assigned-tickets', user?.id, assignedTicketsPage],
    queryFn: async () => {
      if (!user?.id)
        return {
          data: [],
          pagination: { page: 1, limit: assignedTicketsPerPage, totalItems: 0, totalPages: 0 },
        };
      const response = await maintenanceTicketsApi.getAll({
        assignedTo: user.id,
        limit: assignedTicketsPerPage,
        page: assignedTicketsPage,
        sort: 'createdAt',
        order: 'desc',
      });
      return response;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds to check for new tickets
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
  });

  const assignedTickets = assignedTicketsQuery.data?.data ?? [];
  const assignedTicketsPagination = assignedTicketsQuery.data?.pagination;

  // Invalidate assigned tickets query when maintenance tickets queries are invalidated
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // When maintenance-tickets queries are invalidated, also invalidate assigned-tickets
      if (
        event?.type === 'removed' &&
        event.query &&
        (event.query.queryKey[0] === 'maintenance-tickets' ||
          (event.query.queryKey[0] === 'maintenance-ticket' &&
            event.query.state.status === 'success'))
      ) {
        queryClient.invalidateQueries({ queryKey: ['assigned-tickets', user?.id] });
      }
    });

    return () => unsubscribe();
  }, [queryClient, user?.id]);

  const competenceData = competenceQuery.data;
  const competenceStats = [
    {
      label: t('home.stats.qualifications'),
      value: `${competenceData?.competence.activeQualifications ?? 0}/${competenceData?.competence.totalQualifications ?? 0}`,
      icon: GraduationScrollIcon,
      color: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-50 dark:bg-emerald-950/50',
    },
    {
      label: t('home.stats.skillLevel'),
      value: `${competenceData?.competence.completionRate ?? 0}%`,
      icon: Rocket01Icon,
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-950/50',
    },
    {
      label: t('home.stats.trainings'),
      value: `${competenceData?.competence.trainingsCompleted ?? 0}/${competenceData?.competence.trainingsTotal ?? 0}`,
      icon: BookOpen01Icon,
      color: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-50 dark:bg-amber-950/50',
    },
    {
      label: t('home.stats.points'),
      value: pointsTotal.toLocaleString(),
      icon: StarAward01Icon,
      color: 'text-primary',
      bg: 'bg-primary/10 dark:bg-primary/20',
    },
  ];

  const leaderboard = (leaderboardQuery.data ?? [
    {
      id: user?.id ?? '',
      name: user?.name ?? 'You',
      email: user?.email ?? null,
      init: user?.name?.slice(0, 2).toUpperCase() ?? 'YY',
      profilePictureUrl: userQuery.data?.profilePictureUrl ?? null,
      points: pointsTotal,
      level: currentLevel?.level ?? 1,
      rank: 1,
      streak: 0,
      change: 0,
      badges: badges.length,
    },
  ]) as {
    id: string;
    name: string;
    email: string | null;
    profilePictureUrl?: string | null;
    points: number;
    level: number;
    badges: number;
    rank: number;
    init: string;
    streak: number;
    change: number;
  }[];

  // Get the current user's rank from the leaderboard
  const userRank = useMemo(() => {
    const userEntry = leaderboard.find((entry) => entry.name === user?.name);
    return userEntry?.rank ?? 1;
  }, [leaderboard, user?.name]);

  const recentAchievements = badges.map((badge) => ({
    user: user?.name ?? 'You',
    achievement: badge.badge?.name ?? badge.reason ?? t('home.badges.badgeEarned'),
    icon: StarAward01Icon,
    color: 'text-yellow-500',
  }));

  // Pagination hooks
  const _leaderboardPagination = usePagination(leaderboard, 10);
  const achievementsPagination = usePagination(recentAchievements, 5);
  const trainingsPagination = usePagination(trainings, 5);

  // Waiting for role assignment screen
  if (!permissionsLoading && !hasRole) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-muted mb-6">
          <HugeiconsIcon icon={HourglassIcon} size={36} className="text-muted-foreground animate-pulse" />
        </div>
        <h2 className="text-xl font-semibold mb-2">Waiting for Role Assignment</h2>
        <p className="text-sm text-muted-foreground max-w-sm mb-6">
          Your account has been created successfully. An administrator needs to assign you a role before you can access the application.
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-4 py-2.5 rounded-lg">
          <HugeiconsIcon icon={ShieldUserIcon} size={16} />
          <span>Contact your administrator to get access</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* NATIVE MOBILE LAYOUT */}
      <div className="md:hidden">
        {/* Sticky Header with Profile & Level Ring */}
        <MobileNativeHeader
          userName={user?.name ?? 'Operator'}
          avatarUrl={userQuery.data?.profilePictureUrl ?? (user as any)?.image}
          points={pointsTotal}
          level={currentLevel?.level ?? 1}
          levelProgress={levelProgress}
          nextLevel={nextLevel?.level ?? null}
          completedTrainings={
            (profileQuery.data?.trainings || []).filter((t) => t.status === 'completed').length
          }
          rank={userRank}
          tenure={tenure}
          competenceData={competenceData}
        />

        {/* Scrollable Content */}
        <div className="pb-safe">
          {/* Assigned Tickets Section */}
          <NativeListSection
            title={t('home.sections.myTasks')}
            badge={
              assignedTickets.length > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary text-primary-foreground rounded-full">
                  {assignedTickets.length}
                </span>
              )
            }
          >
            {assignedTicketsQuery.isLoading ? (
              <div className="px-3 py-8 text-center">
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <p className="text-xs text-muted-foreground mt-2">{t('home.tickets.loading')}</p>
              </div>
            ) : assignedTickets.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <div className="h-10 w-10 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
                  <HugeiconsIcon
                    icon={TaskDone01Icon}
                    size={20}
                    className="text-muted-foreground"
                  />
                </div>
                <p className="text-xs text-muted-foreground">{t('home.tickets.noTickets')}</p>
              </div>
            ) : (
              <>
                {assignedTickets.slice(0, 5).map((ticket: any) => (
                  <NativeListCell
                    key={ticket.id}
                    icon={
                      <HugeiconsIcon icon={TaskDone01Icon} size={18} className="text-blue-500" />
                    }
                    iconClassName="bg-blue-500/10"
                    title={<EllipsisText maxWidth="180px">{ticket.title}</EllipsisText>}
                    detail={`${t('home.tickets.priorityLabel', { priority: ticket.priority })} • ${ticket.type}`}
                    badge={
                      <Badge
                        variant={getStatusBadge(ticket.status).variant}
                        className="text-[10px] px-1.5 py-0 h-5"
                      >
                        {getStatusBadge(ticket.status).label}
                      </Badge>
                    }
                    onClick={() =>
                      navigate({ to: '/tickets/$ticketId', params: { ticketId: ticket.id } })
                    }
                  />
                ))}
                {assignedTickets.length > 5 && (
                  <Link to="/tickets" className="block">
                    <NativeListCell
                      icon={
                        <HugeiconsIcon
                          icon={ArrowRight02Icon}
                          size={18}
                          className="text-muted-foreground"
                        />
                      }
                      iconClassName="bg-muted/50"
                      title={t('home.tickets.viewAll', { count: assignedTickets.length })}
                      chevron={false}
                    />
                  </Link>
                )}
              </>
            )}
          </NativeListSection>

          {/* Trainings Section - only pending/in-progress */}
          <NativeListSection title={t('home.sections.myDevelopment')}>
            {trainings.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <div className="h-10 w-10 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
                  <HugeiconsIcon
                    icon={GraduationScrollIcon}
                    size={20}
                    className="text-muted-foreground"
                  />
                </div>
                <p className="text-xs text-muted-foreground">{t('home.trainings.noTrainings')}</p>
              </div>
            ) : (
              <>
                {trainings
                  .filter((tr) => tr.status !== 'completed')
                  .slice(0, 3)
                  .map((training) => (
                    <div
                      key={training.id}
                      className="px-3 py-3 border-b border-border/50 last:border-b-0"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                          <HugeiconsIcon
                            icon={BookOpen01Icon}
                            size={18}
                            className="text-emerald-500"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {training.training?.name ?? t('home.trainings.training')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {training.dueDate
                              ? `${t('home.trainings.due')} ${new Date(training.dueDate).toLocaleDateString()}`
                              : t('home.trainings.noDueDate')}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] px-1.5 py-0 h-5 shrink-0',
                            training.status === 'in-progress'
                              ? 'border-blue-500/30 text-blue-500 bg-blue-500/10'
                              : 'border-muted-foreground/30 text-muted-foreground bg-muted/50',
                          )}
                        >
                          {training.status}
                        </Badge>
                      </div>
                      <NativeProgressBar value={training.progress ?? 0} color="primary" />
                    </div>
                  ))}
                {trainings.filter((tr) => tr.status !== 'completed').length === 0 && (
                  <div className="px-3 py-4 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <HugeiconsIcon icon={TaskDone01Icon} size={18} className="text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-emerald-600">
                        All trainings completed!
                      </p>
                    </div>
                  </div>
                )}
                {trainings.length > 3 && (
                  <Link to="/training" className="block">
                    <NativeListCell
                      icon={
                        <HugeiconsIcon
                          icon={ArrowRight02Icon}
                          size={18}
                          className="text-muted-foreground"
                        />
                      }
                      iconClassName="bg-muted/50"
                      title={t('home.trainings.viewAll', { count: trainings.length })}
                      chevron={false}
                    />
                  </Link>
                )}
              </>
            )}
          </NativeListSection>

          {/* Compact Leaderboard - top 3 + your rank */}
          <NativeListSection title={t('home.rank.leaderboard')} badge={undefined}>
            {leaderboard.slice(0, 3).map((tech) => (
              <div
                key={tech.name}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 border-b border-border/50 last:border-b-0',
                  tech.rank === 1 && 'bg-gradient-to-r from-primary/5 to-accent/5',
                  tech.name === user?.name && 'bg-primary/5',
                )}
              >
                <div className="shrink-0 w-8 text-center">
                  {tech.rank === 1 ? (
                    <HugeiconsIcon
                      icon={ChampionIcon}
                      size={18}
                      className="text-amber-500 mx-auto"
                    />
                  ) : tech.rank === 2 ? (
                    <HugeiconsIcon
                      icon={Medal01Icon}
                      size={18}
                      className="text-muted-foreground mx-auto"
                    />
                  ) : (
                    <HugeiconsIcon
                      icon={Award01Icon}
                      size={18}
                      className="text-amber-700 mx-auto"
                    />
                  )}
                </div>
                <Avatar className="h-8 w-8">
                  {tech.profilePictureUrl && (
                    <AvatarImage src={tech.profilePictureUrl} alt={tech.name} />
                  )}
                  <AvatarFallback className="text-[10px] font-semibold bg-gradient-to-br from-primary to-accent text-white">
                    {tech.init}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {tech.name}
                    {tech.name === user?.name && (
                      <span className="text-xs text-primary ml-1">(You)</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 text-xs font-semibold tabular-nums shrink-0">
                  <HugeiconsIcon icon={StarAward01Icon} size={12} className="text-amber-500" />
                  {tech.points.toLocaleString()}
                </div>
              </div>
            ))}
          </NativeListSection>

          {/* Bottom padding for safe area + FAB */}
          <div className="h-20" />
        </div>
      </div>

      {/* DESKTOP LAYOUT (unchanged) */}
      <div className="hidden md:flex flex-col gap-3 sm:gap-4">
        {/* Hero Section - Personal Stats */}
        <div className="grid gap-3 sm:gap-4 md:grid-cols-4">
          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
                  {t('home.greeting', { name: user?.name ?? 'Operator' })}
                </h1>
                <span className="text-lg">👋</span>
              </div>
              {tenure && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                  <HugeiconsIcon icon={Calendar02Icon} size={14} className="text-primary" />
                  <span className="text-sm font-medium text-primary">
                    {getTenureDisplayShort(tenure)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    ({tenure.totalDays} {t('home.mobile.days')})
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 mb-3 sm:mb-4">
              <p className="text-xs sm:text-sm text-muted-foreground">
                Your latest training, badges, and points are up to date.
              </p>
              {tenure && motivationalQuote && (
                <p className="text-xs italic text-muted-foreground text-right shrink-0">
                  "{motivationalQuote.quote}"
                </p>
              )}
            </div>
            {/* Quick Stats */}
            <div className="grid gap-2 sm:gap-3 grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
              {competenceStats.map((stat) => (
                <Card key={stat.label} className="py-3 gap-2 metric-card">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                    <CardTitle className="text-xs font-medium pb-0 uppercase tracking-wide text-muted-foreground">
                      {stat.label}
                    </CardTitle>
                    <div className={`${stat.bg} p-2 rounded-md`}>
                      <HugeiconsIcon icon={stat.icon} size={16} className={stat.color} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="metric-value">{stat.value}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <Card className="gap-3 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 dark:from-primary/10 dark:to-accent/10">
            <CardContent className="pt-0">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-0.5 uppercase tracking-wide">
                    {t('home.rank.yourRank')}
                  </p>
                  <div className="flex items-baseline gap-1.5">
                    <HugeiconsIcon icon={ChampionIcon} size={20} className="text-primary" />
                    <span className="text-2xl font-bold">#{userRank}</span>
                  </div>
                </div>
                <ActivityRings
                  size={56}
                  rings={[
                    { value: competenceData?.competence.completionRate ?? 0, color: '#10b981' },
                    { value: levelProgress, color: 'hsl(var(--primary))' },
                    {
                      value: Math.min(100, (badges.length / Math.max(badges.length + 2, 5)) * 100),
                      color: '#f59e0b',
                    },
                  ]}
                />
              </div>
              <div className="pt-3 border-t border-border">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-medium">
                    {competenceData?.competence.activeQualifications ?? 0}{' '}
                    {t('home.stats.qualifications')}
                  </span>
                  <span className="text-muted-foreground font-technical">
                    Lvl {currentLevel?.level ?? 1} · {pointsTotal.toLocaleString()} pts
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-3 sm:gap-4 lg:grid-cols-4">
          {/* Leaderboard - 1/4 screen */}
          <Card className="lg:col-span-1 gap-2 order-2 lg:order-1">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-base pb-0">
                <HugeiconsIcon icon={ChampionIcon} size={16} className="text-primary" />
                {t('home.competitions.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {competitionsQuery.isLoading ? (
                <div className="text-center py-4 text-xs text-muted-foreground">Loading...</div>
              ) : (competitionsQuery.data ?? []).length === 0 ? (
                <>
                  <div className="text-center py-2 text-xs text-muted-foreground mb-2">
                    {t('home.competitions.none')}
                  </div>
                  {/* Fallback: traditional leaderboard */}
                  <div className="space-y-2">
                    {leaderboard.slice(0, 5).map((tech) => (
                      <div
                        key={tech.name}
                        className={`flex items-center gap-2 p-2 rounded-md border transition-all ${
                          tech.name === user?.name
                            ? 'bg-primary/5 border-primary/20'
                            : 'hover:bg-accent/50'
                        }`}
                      >
                        <div className="shrink-0 w-6 text-center">
                          {tech.rank <= 3 ? (
                            <HugeiconsIcon
                              icon={tech.rank === 1 ? ChampionIcon : Medal01Icon}
                              size={14}
                              className="text-primary mx-auto"
                            />
                          ) : (
                            <span className="text-xs font-bold text-muted-foreground">
                              #{tech.rank}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-semibold truncate">{tech.name}</span>
                        </div>
                        <span className="text-[10px] font-technical text-muted-foreground">
                          {tech.points.toLocaleString()} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  {(competitionsQuery.data ?? []).map((comp: any) => (
                    <div key={comp.id} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold truncate">{comp.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {t('home.competitions.endsOn', {
                            date: new Date(comp.endsAt).toLocaleDateString(),
                          })}
                        </span>
                      </div>
                      {(comp.standings ?? []).slice(0, 3).map((s: any, i: number) => (
                        <div
                          key={s.id}
                          className={`flex items-center gap-2 p-1.5 rounded-md text-xs ${
                            s.userId === user?.id ? 'bg-primary/10 border border-primary/20' : ''
                          }`}
                        >
                          <span className="w-4 text-center font-bold text-muted-foreground">
                            {i + 1}
                          </span>
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="text-[8px]">
                              {(s.userName ?? '??').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="flex-1 truncate font-medium">{s.userName}</span>
                          <span className="font-technical font-semibold">{s.score}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Main Content - 3/4 screen */}
          <div className="lg:col-span-3 space-y-3 sm:space-y-4 order-1 lg:order-2">
            {/* Development & Gamification */}
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              {/* Your Badges & Achievements */}
              <Card className="gap-2 border-purple-200/50 dark:border-purple-900/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <HugeiconsIcon
                      icon={Award01Icon}
                      size={16}
                      className="text-purple-600 dark:text-purple-400"
                    />
                    {t('home.badges.title')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t('home.badges.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {badges.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('home.badges.noBadges')}</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {badges.map((badge) => (
                        <Badge
                          key={badge.id}
                          variant="outline"
                          className="gap-1 py-1 px-2 text-xs border-purple-200/50 dark:border-purple-800/50"
                        >
                          <HugeiconsIcon
                            icon={StarAward01Icon}
                            size={10}
                            className="text-purple-600 dark:text-purple-400"
                          />
                          {badge.badge?.name ??
                            badge.reason ??
                            `Badge ${badge.badgeId.slice(0, 6)}`}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">
                      {t('home.sections.nextAchievement')}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <HugeiconsIcon icon={Rocket01Icon} size={14} className="text-primary" />
                      <span className="text-xs font-medium">
                        {t('home.achievements.nextAchievement')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Achievements */}
              <Card className="gap-2 border-emerald-200/50 dark:border-emerald-900/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <HugeiconsIcon
                      icon={User02Icon}
                      size={16}
                      className="text-emerald-600 dark:text-emerald-400"
                    />
                    {t('home.achievements.teamAchievements')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t('home.achievements.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {recentAchievements.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {t('home.achievements.noAchievements')}
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {achievementsPagination.paginatedItems.map((achievement, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 p-2 rounded-md hover:bg-accent/50 transition-colors border border-transparent hover:border-border/50"
                          >
                            <div className={`p-1.5 rounded-md bg-primary/10`}>
                              <HugeiconsIcon
                                icon={achievement.icon}
                                size={14}
                                className={`${achievement.color}`}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{achievement.user}</p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {achievement.achievement}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <PaginationControls
                        currentPage={achievementsPagination.currentPage}
                        totalPages={achievementsPagination.totalPages}
                        onPageChange={achievementsPagination.goToPage}
                        onPrev={achievementsPagination.goToPrevPage}
                        onNext={achievementsPagination.goToNextPage}
                        hasPrev={achievementsPagination.hasPrevPage}
                        hasNext={achievementsPagination.hasNextPage}
                      />
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Operational Overview - Trainings & Assigned Tickets */}
            <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
              <Card className="gap-2">
                <CardHeader className="pb-0">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <HugeiconsIcon icon={GraduationScrollIcon} size={16} />
                    {t('home.trainings.assigned')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t('home.trainings.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  {trainings.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {t('home.trainings.noTrainings')}
                    </p>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className="hidden md:block rounded-md p-2 border bg-sidebar/20">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="h-8 text-xs">
                                {t('home.trainings.training')}
                              </TableHead>
                              <TableHead className="h-8 text-xs">
                                {t('home.trainings.status')}
                              </TableHead>
                              <TableHead className="h-8 text-xs">
                                {t('home.trainings.due')}
                              </TableHead>
                              <TableHead className="h-8 text-xs">
                                {t('home.trainings.progress')}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {trainingsPagination.paginatedItems.map((training) => (
                              <TableRow key={training.id}>
                                <TableCell className="text-xs font-medium">
                                  {training.training?.name ?? t('home.trainings.training')}
                                </TableCell>
                                <TableCell className="text-xs capitalize">
                                  <Badge variant="outline" className="text-[10px]">
                                    {training.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs">
                                  {training.dueDate
                                    ? new Date(training.dueDate).toLocaleDateString()
                                    : '-'}
                                </TableCell>
                                <TableCell className="w-[140px]">
                                  <div className="flex items-center gap-1.5">
                                    <Progress
                                      value={training.progress ?? 0}
                                      className="h-1.5 flex-1"
                                    />
                                    <span className="text-[10px] tabular-nums text-muted-foreground">
                                      {training.progress ?? 0}%
                                    </span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <PaginationControls
                        currentPage={trainingsPagination.currentPage}
                        totalPages={trainingsPagination.totalPages}
                        onPageChange={trainingsPagination.goToPage}
                        onPrev={trainingsPagination.goToPrevPage}
                        onNext={trainingsPagination.goToNextPage}
                        hasPrev={trainingsPagination.hasPrevPage}
                        hasNext={trainingsPagination.hasNextPage}
                      />
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="gap-2 hidden md:block">
                <CardHeader className="pb-0">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <HugeiconsIcon icon={TaskDone01Icon} size={16} />
                    {t('home.tickets.assigned')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t('home.tickets.description')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  {assignedTicketsQuery.isLoading ? (
                    <p className="text-xs text-muted-foreground">{t('home.tickets.loading')}</p>
                  ) : assignedTickets.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('home.tickets.noTickets')}</p>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className="hidden md:block rounded-md p-2 border bg-sidebar/20">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="h-8 text-xs">
                                {t('home.tickets.title')}
                              </TableHead>
                              <TableHead className="h-8 text-xs">
                                {t('home.tickets.status')}
                              </TableHead>
                              <TableHead className="h-8 text-xs">
                                {t('home.tickets.priority')}
                              </TableHead>
                              <TableHead className="h-8 text-xs">
                                {t('home.tickets.type')}
                              </TableHead>
                              <TableHead className="h-8 text-xs">
                                {t('actionsColumn.view')}
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {assignedTickets.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={5}
                                  className="h-24 text-center text-xs text-muted-foreground"
                                >
                                  {t('home.tickets.noTickets')}
                                </TableCell>
                              </TableRow>
                            ) : (
                              assignedTickets.map((ticket: any) => {
                                const statusBadge = getStatusBadge(ticket.status);
                                const priorityBadge = getPriorityBadge(ticket.priority);
                                return (
                                  <TableRow key={ticket.id}>
                                    <TableCell className="text-xs font-medium">
                                      <EllipsisText maxWidth="200px">{ticket.title}</EllipsisText>
                                    </TableCell>
                                    <TableCell className="text-xs">
                                      <Badge variant={statusBadge.variant} className="text-[10px]">
                                        {statusBadge.label}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs">
                                      <Badge
                                        variant={priorityBadge.variant}
                                        className="text-[10px]"
                                      >
                                        {priorityBadge.label}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs capitalize">
                                      <Badge variant="secondary" className="text-[10px]">
                                        {ticket.type}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <Button variant="outline" size="sm" asChild>
                                        <Link
                                          to="/tickets/$ticketId"
                                          params={{ ticketId: ticket.id }}
                                        >
                                          {t('actionsColumn.view')}
                                        </Link>
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      {assignedTicketsPagination && assignedTicketsPagination.totalPages > 1 && (
                        <PaginationControls
                          currentPage={assignedTicketsPage}
                          totalPages={assignedTicketsPagination.totalPages}
                          onPageChange={setAssignedTicketsPage}
                          onPrev={() => setAssignedTicketsPage((p) => Math.max(1, p - 1))}
                          onNext={() =>
                            setAssignedTicketsPage((p) =>
                              Math.min(assignedTicketsPagination.totalPages, p + 1),
                            )
                          }
                          hasPrev={assignedTicketsPage > 1}
                          hasNext={assignedTicketsPage < assignedTicketsPagination.totalPages}
                        />
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Activity className="h-4 w-4" />
                Rule Lab
              </CardTitle>
              <CardDescription className="text-xs">Trigger a rule to validate points and badge logic</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Select rule</label>
                  <select
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={selectedRuleId || ruleOptions[0]?.id || ''}
                    onChange={(e) => setSelectedRuleId(e.target.value)}
                  >
                    {ruleOptions.map((rule) => (
                      <option key={rule.id} value={rule.id}>{rule.name}</option>
                    ))}
                  </select>
                </div>
                <Button
                  className="mt-2 md:mt-7"
                  onClick={() => {
                    const targetRuleId = selectedRuleId || ruleOptions[0]?.id
                    if (!targetRuleId) return
                    ruleTestMutation.mutate(targetRuleId)
                  }}
                  disabled={ruleTestMutation.isPending || (ruleOptions.length === 0 && !selectedRuleId)}
                >
                  {ruleTestMutation.isPending ? 'Testing...' : 'Test rule'}
                </Button>
              </div>
              {ruleTestMutation.isSuccess && (
                <p className="text-xs text-emerald-600">Rule executed. Check profile to see awarded points or badges.</p>
              )}
              {ruleTestMutation.isError && (
                <p className="text-xs text-destructive">Failed to run rule. Ensure a rule is selected.</p>
              )}
            </CardContent>
          </Card> */}
          </div>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute('/_authenticated/home')({
  component: Home,
});
